import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plug, PlusCircle, Trash2, RefreshCw, CheckCircle2, XCircle, Power, PowerOff, Edit3 } from "lucide-react";

/**
 * Connectors — tenant-scoped credentialed bridges to external systems.
 * Each connector powers real playbook steps and/or auto-evaluated compliance controls.
 */
export default function Connectors() {
  const [types, setTypes] = useState({});
  const [connectors, setConnectors] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", type: "slack_webhook", enabled: true, config: {} });
  const [testing, setTesting] = useState(null);

  const load = async () => {
    const [t, c] = await Promise.all([
      api.get("/admin/connectors/types"),
      api.get("/admin/connectors"),
    ]);
    setTypes(t.data.types || {});
    setConnectors(c.data.connectors || []);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", type: "slack_webhook", enabled: true, config: {} });
    setShowForm(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, type: c.type, enabled: c.enabled, config: {} });
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      if (editing) {
        // For edit: send only non-empty config fields so secrets aren't wiped
        const cleanCfg = {};
        Object.entries(form.config).forEach(([k, v]) => { if (v !== "" && v !== undefined) cleanCfg[k] = v; });
        await api.patch(`/admin/connectors/${editing.id}`, {
          name: form.name, enabled: form.enabled, config: cleanCfg,
        });
        toast.success("Connector updated");
      } else {
        await api.post("/admin/connectors", form);
        toast.success("Connector created");
      }
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete connector "${c.name}"?`)) return;
    await api.delete(`/admin/connectors/${c.id}`);
    toast.success("Deleted");
    load();
  };

  const test = async (c) => {
    setTesting(c.id);
    try {
      const r = await api.post(`/admin/connectors/${c.id}/test`);
      r.data.ok ? toast.success(`✓ ${r.data.detail}`) : toast.error(`✗ ${r.data.detail}`);
      load();
    } finally { setTesting(null); }
  };

  const toggle = async (c) => {
    await api.patch(`/admin/connectors/${c.id}`, { enabled: !c.enabled });
    load();
  };

  const selType = types[form.type] || { config_fields: [], actions: [], evaluators: [] };

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="connectors-page">
      <header className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center gap-2">
            <Plug className="w-3.5 h-3.5" /> Tenant-scoped bridges to external systems
          </div>
          <h1 className="font-display font-black text-5xl mt-2">Connectors</h1>
          <p className="text-[#8E9196] mt-1">
            Wire real actions to playbook steps and real evidence collection to compliance controls.
            Each connector is <span className="text-[#00F0FF]">scoped to your tenant only</span> — credentials never leak across companies.
          </p>
        </div>
        <button onClick={openNew} data-testid="new-connector-btn"
          className="inline-flex items-center gap-2 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">
          <PlusCircle className="w-3.5 h-3.5" /> New Connector
        </button>
      </header>

      {/* Health strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="health-strip">
        {Object.keys(types).map((t) => {
          const bucket = connectors.filter((c) => c.type === t);
          const healthy = bucket.filter((c) => c.enabled && c.last_test_ok === true).length;
          const failing = bucket.filter((c) => c.last_test_ok === false).length;
          return (
            <div key={t} className="border border-white/10 bg-[#0a0a0a] rounded-sm p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">{types[t].label}</div>
              <div className="mt-2 flex items-baseline gap-3">
                <span className="font-display font-black text-2xl">{bucket.length}</span>
                <span className="text-[10px] font-mono text-[#34C759]">{healthy} healthy</span>
                {failing > 0 && <span className="text-[10px] font-mono text-[#FF3B30]">{failing} fail</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Connector list */}
      <div className="border border-white/10 bg-[#121212] rounded-sm" data-testid="connectors-list">
        {connectors.length === 0 && (
          <div className="p-8 text-center text-[#8E9196] text-sm">
            No connectors yet. Click <span className="text-[#00F0FF]">New Connector</span> to wire one up.
          </div>
        )}
        {connectors.map((c) => {
          const meta = types[c.type] || {};
          return (
            <div key={c.id} className="border-b border-white/5 last:border-b-0 px-5 py-4 flex items-center gap-4"
              data-testid={`connector-${c.id}`}>
              <div className={`w-2 h-8 shrink-0 ${c.enabled ? "bg-[#00F0FF]" : "bg-[#52525B]"}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-display text-lg truncate">{c.name}</div>
                  {c.last_test_ok === true && <CheckCircle2 className="w-4 h-4 text-[#34C759]" />}
                  {c.last_test_ok === false && <XCircle className="w-4 h-4 text-[#FF3B30]" />}
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mt-0.5">
                  {meta.label || c.type} · created by {c.created_by} · {c.enabled ? "enabled" : "disabled"}
                  {c.last_test_detail && <span> · last test: <span className="text-white">{c.last_test_detail}</span></span>}
                </div>
                <div className="mt-1 flex gap-1.5 flex-wrap">
                  {(meta.actions || []).map((a) => (
                    <span key={a} className="text-[9px] font-mono uppercase tracking-widest text-[#00F0FF] border border-[#00F0FF]/40 px-1.5 py-0.5">action:{a}</span>
                  ))}
                  {(meta.evaluators || []).map((e) => (
                    <span key={e} className="text-[9px] font-mono uppercase tracking-widest text-[#B57BFF] border border-[#B57BFF]/40 px-1.5 py-0.5">eval:{e}</span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button onClick={() => test(c)} disabled={testing === c.id} data-testid={`test-${c.id}`}
                  className="p-2 border border-white/10 hover:border-[#00F0FF] hover:text-[#00F0FF] rounded-sm disabled:opacity-50">
                  <RefreshCw className={`w-3.5 h-3.5 ${testing === c.id ? "animate-spin" : ""}`} />
                </button>
                <button onClick={() => toggle(c)} data-testid={`toggle-${c.id}`}
                  className="p-2 border border-white/10 hover:border-[#00F0FF] hover:text-[#00F0FF] rounded-sm">
                  {c.enabled ? <Power className="w-3.5 h-3.5" /> : <PowerOff className="w-3.5 h-3.5" />}
                </button>
                <button onClick={() => openEdit(c)} data-testid={`edit-${c.id}`}
                  className="p-2 border border-white/10 hover:border-[#00F0FF] hover:text-[#00F0FF] rounded-sm">
                  <Edit3 className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => del(c)} data-testid={`del-${c.id}`}
                  className="p-2 border border-white/10 hover:border-[#FF3B30] hover:text-[#FF3B30] rounded-sm">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add / Edit dialog */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && setShowForm(false)} data-testid="connector-form">
          <div className="bg-[#0a0a0a] border border-white/15 rounded-sm max-w-lg w-full p-6">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] mb-1">
              {editing ? "Edit connector" : "New connector"}
            </div>
            <div className="font-display font-black text-2xl mb-4">
              {editing ? editing.name : "Wire an integration"}
            </div>

            {!editing && (
              <div className="mb-4">
                <label className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">Type</label>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value, config: {} })}
                  data-testid="connector-type"
                  className="w-full mt-1 bg-transparent border border-white/15 px-3 py-2 rounded-sm text-sm">
                  {Object.entries(types).map(([k, v]) => (
                    <option key={k} value={k} className="bg-[#0a0a0a]">{v.label}</option>
                  ))}
                </select>
                <div className="text-[11px] text-[#8E9196] mt-1">{selType.description}</div>
              </div>
            )}

            <div className="mb-4">
              <label className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="connector-name"
                placeholder="e.g. Acme Slack, Prod AWS"
                className="w-full mt-1 bg-transparent border border-white/15 px-3 py-2 rounded-sm text-sm font-mono outline-none focus:border-[#00F0FF]" />
            </div>

            {selType.config_fields?.map((f) => (
              <div key={f.name} className="mb-3">
                <label className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">
                  {f.label}{f.required && <span className="text-[#FF3B30] ml-1">*</span>}
                </label>
                <input
                  type={f.type === "secret" ? "password" : (f.type === "number" ? "number" : "text")}
                  value={form.config[f.name] || ""}
                  onChange={(e) => setForm({ ...form, config: { ...form.config, [f.name]: e.target.value } })}
                  data-testid={`connector-field-${f.name}`}
                  placeholder={editing && f.type === "secret" ? "•••• (leave blank to keep existing)" : f.placeholder}
                  className="w-full mt-1 bg-transparent border border-white/15 px-3 py-2 rounded-sm text-sm font-mono outline-none focus:border-[#00F0FF]"
                />
              </div>
            ))}

            <div className="flex items-center gap-2 mt-4 mb-6">
              <input type="checkbox" id="cn-enabled" checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="connector-enabled" />
              <label htmlFor="cn-enabled" className="text-sm text-[#e5e5e5]">Enabled</label>
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={() => setShowForm(false)}
                className="px-3 py-2 border border-white/15 text-white/80 rounded-sm font-mono uppercase text-xs tracking-widest">
                Cancel
              </button>
              <button onClick={submit} data-testid="submit-connector"
                className="px-3 py-2 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black rounded-sm font-mono uppercase text-xs tracking-widest">
                {editing ? "Save" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
